import React from 'react';
import Drawer from '../ui/Drawer';
import Alert from '../ui/Alert';
import Select from '../ui/Select';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import PushFields from '../messaging/PushFields';
import WhatsappFields from '../messaging/WhatsappFields';
import SmsFields from '../messaging/SmsFields';
import EmailFields from '../messaging/EmailFields';
import RcsFields from '../messaging/RcsFields';
import SegmentBuilder from '../audience/SegmentBuilder';
import PropertyFilters from '../audience/PropertyFilters';
import { NODE_DEFS } from '../../data/nodeDefinitions';
import { segmentService } from '../../services/segmentService';
import styles from './ConfigDrawer.module.css';

// Channel action nodes render the shared Live Preview (a 1fr + 240px grid), so
// their drawer is widened to fit the preview beside the form.
const MESSAGE_ACTION_TYPES = new Set(['action-push', 'action-whatsapp', 'action-email', 'action-sms', 'action-rcs']);

function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

// A best-effort "resolved next execution" preview (relative to now; the engine
// resolves relative to step entry, so this is an approximation — hence "≈").
function waitUntilPreview(cfg) {
  const now = new Date();
  const fmt = (d) => d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const [h, m] = (cfg.time || '09:00').split(':').map(Number);
  const setT = (d) => { d.setHours(Number.isFinite(h) ? h : 9, Number.isFinite(m) ? m : 0, 0, 0); return d; };
  try {
    switch (cfg.rule) {
      case 'tomorrow': { const d = new Date(now); d.setDate(d.getDate() + 1); return 'Next run ≈ ' + fmt(setT(d)); }
      case 'n_days': { const d = new Date(now); d.setDate(d.getDate() + (Number(cfg.days) || 1)); return 'Next run ≈ ' + fmt(setT(d)); }
      case 'next_weekday': { const d = new Date(now); const t = Number(cfg.weekday); do { d.setDate(d.getDate() + 1); } while (d.getDay() !== t); return 'Next run ≈ ' + fmt(setT(d)); }
      case 'monthly_day': { const d = new Date(now); const dom = Number(cfg.dayOfMonth) || 1; d.setDate(Math.min(dom, daysInMonth(d))); setT(d); if (d <= now) { d.setMonth(d.getMonth() + 1); d.setDate(Math.min(dom, daysInMonth(d))); setT(d); } return 'Next run ≈ ' + fmt(d); }
      case 'last_day_of_month': { const d = new Date(now); const back = Number(cfg.daysBeforeEnd) || 0; d.setDate(daysInMonth(d) - back); setT(d); if (d <= now) { d.setMonth(d.getMonth() + 1); d.setDate(daysInMonth(d) - back); setT(d); } return 'Next run ≈ ' + fmt(d); }
      case 'specific': return cfg.customDate ? ('Runs at ' + cfg.customDate + ' ' + (cfg.customTime || '00:00')) : 'Pick a date & time';
      case 'next_day_noon': return 'Next run ≈ tomorrow 12:00 PM';
      case 'next_monday': return 'Next run ≈ next Monday 9:00 AM';
      case 'end_of_week': return 'Next run ≈ end of week (Sun 23:59)';
      default: return '';
    }
  } catch { return ''; }
}

function Field({ label, children }) {
  return (
    <div className={styles.field}>
      <label className={styles.label}>{label}</label>
      {children}
    </div>
  );
}

// Every call site elsewhere in this file keeps its {value, onChange, ...}
// contract unchanged — only the rendering underneath moved to the shared
// Input component.
function Inp({ value, onChange, placeholder, type = 'text' }) {
  return (
    <Input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

// Every call site elsewhere in this file keeps its {value, onChange, options}
// contract unchanged — only the rendering underneath moved to the shared
// searchable Select component.
function Sel({ value, onChange, options }) {
  const normalized = options.map(o => (
    typeof o === 'string'
      ? { value: o, label: o }
      : { value: o.value, label: o.label + (o.disabled ? ' (coming soon)' : ''), disabled: o.disabled }
  ));
  return <Select value={value || ''} options={normalized} onChange={onChange} />;
}

function TextArea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <Textarea
      rows={rows}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
}

class ConfigDrawer extends React.Component {
  constructor(props) {
    super(props);
    this.state = { segments: [], events: [] };
  }

  componentDidMount() {
    segmentService.getAll().then((segments) => this.setState({ segments })).catch(() => {});
    // Same live discovery the Segment Builder's FilterRow.jsx already uses,
    // instead of data/events.js's hardcoded APP_EVENTS list — real event
    // names actually seen in ClickHouse, not dummy seeded ones. Scoped to
    // the plain `events` table specifically (not lead_events/earning_events/
    // task_events) because that's the only table the journey engine's Event
    // Trigger/Goal Check actually query (engine.service.js's
    // eventTriggerUserIds/evaluateGoal) — offering an event from a table the
    // engine can't check yet would silently never fire.
    segmentService.getOptions()
      .then((options) => {
        const seen = new Set();
        const events = (options.events || [])
          .filter((e) => e.table === 'events' && !seen.has(e.name) && seen.add(e.name))
          .map((e) => e.name);
        this.setState({ events });
      })
      .catch(() => {});
  }

  render() {
    const { node, open, onClose, onChange } = this.props;
    if (!node) return null;

    const def = NODE_DEFS[node.type] || {};
    const cfg = node.cfg || {};
    const set = key => val => onChange({ ...cfg, [key]: val });
    const onFieldChange = (field, val) => onChange({ ...cfg, [field]: val });

    const fields = this._fields(node.type, cfg, set, onFieldChange, this.state.segments);

    return (
      <Drawer
        open={open}
        onClose={onClose}
        title={def.label}
        subtitle="Configure this step"
        ico={def.ico}
        icoStyle={{ background: def.icoC, color: def.icoT }}
        width={
          (node.type === 'entry-segment' || node.type === 'entry') ? '520px'
            : MESSAGE_ACTION_TYPES.has(node.type) ? '640px' // room for the side-by-side Live Preview (1fr + 240px)
            : '360px'
        }
        footer={
          <button className={styles.saveBtn} onClick={onClose}>
            Done
          </button>
        }
      >
        <div className={styles.body}>
          {def.comingSoon && (
            <div style={{ marginBottom: 16 }}>
              <Alert type="warning" title="Not run by the journey engine yet">
                This step type is here for planning — the journey engine doesn't execute it yet. It'll be skipped when you publish.
              </Alert>
            </div>
          )}
          {fields}
        </div>
      </Drawer>
    );
  }

  // Unified Entry block — audience, timing and re-entry in ONE panel, so an
  // "audience AND schedule" entry (e.g. "every Wed 5pm to Segment 1") no longer
  // needs two connected trigger nodes. Modes/policies the journey engine can't
  // run yet are shown but disabled in their dropdowns (honest capability state),
  // so a publishable Entry can only ever compile to something the engine runs.
  _entryFields(cfg, set, segments) {
    const { onChange, node } = this.props;
    const mode = cfg.mode || 'segment';
    const modeOptions = [
      { value: 'segment',   label: 'Segment membership' },
      { value: 'event',     label: 'Event occurrence' },
      { value: 'schedule',  label: 'Scheduled audience' },
      { value: 'attribute', label: 'User attribute change', disabled: true },
      { value: 'manual',    label: 'Manual / list entry', disabled: true },
    ];

    return (
      <>
        <Field label="Entry mode">
          <Sel value={mode} onChange={set('mode')} options={modeOptions} />
          <div className={styles.hint}>How users enter this journey. Audience and timing are configured separately below — no need to chain two triggers.</div>
        </Field>

        {mode === 'segment' && (
          <>
            <Field label="Audience segment">
              <SegmentBuilder
                onChange={(audience) => {
                  if (audience.mode === 'saved' && audience.selectedSegId) {
                    const seg = segments.find((s) => s.id === audience.selectedSegId);
                    onChange({ ...cfg, segmentId: audience.selectedSegId, segmentName: seg?.name || '' });
                  }
                }}
                onSaved={(segment) => onChange({ ...cfg, segmentId: segment.id, segmentName: segment.name })}
              />
            </Field>
            <Field label="Membership">
              <Sel value={cfg.membershipMode} onChange={set('membershipMode')} options={[
                { value: 'current_and_future', label: 'Current members + future entrants' },
                { value: 'on_enter', label: 'Only when a user enters the segment', disabled: true },
                { value: 'on_exit',  label: 'When a user leaves the segment', disabled: true },
              ]} />
              <div className={styles.hint}>The segment is re-evaluated every ~15 min; matching users are admitted (deduplicated). Enter/exit transitions arrive with the engine stage.</div>
            </Field>
          </>
        )}

        {mode === 'event' && (
          <>
            <Field label="Trigger event">
              <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
            </Field>
            <Field label="Lookback window">
              <div className={styles.row2}>
                <Inp value={cfg.lookbackValue} onChange={set('lookbackValue')} placeholder="7" type="number" />
                <Sel value={cfg.lookbackUnit} onChange={set('lookbackUnit')} options={['hours', 'days', 'weeks', 'months']} />
              </div>
              <div className={styles.hint}>Admits users who fired this event within the window, checked every ~15 min.</div>
            </Field>
            <Field label="Event properties (optional)">
              <PropertyFilters
                propFilters={cfg.propFilters || []}
                onChange={set('propFilters')}
                eventName={cfg.event}
                eventTable="events"
                idPrefix={node.id}
              />
              <div className={styles.hint}>Enforced by the engine — only users whose event matched every condition are admitted.</div>
            </Field>
          </>
        )}

        {mode === 'schedule' && (
          <>
            <Field label="Audience segment">
              <Sel
                value={cfg.segmentId}
                onChange={(val) => {
                  const seg = segments.find((s) => s.id === val);
                  onChange({ ...cfg, segmentId: val, segmentName: seg?.name || '' });
                }}
                options={segments.map((s) => ({ value: s.id, label: s.name }))}
              />
              <div className={styles.hint}>Current membership is re-evaluated fresh on every scheduled run — each eligible user is admitted at most once per occurrence.</div>
            </Field>
            <Field label="Run schedule (cron)">
              <Inp value={cfg.cron} onChange={set('cron')} placeholder="0 17 * * 3" />
              <div className={styles.hint}>e.g. <code>0 17 * * 3</code> = every Wednesday 5 PM. Checked every ~15 min, so sub-15-min schedules won't fire on every intended tick.</div>
            </Field>
            <Field label="Timezone">
              <Sel value={cfg.tz} onChange={set('tz')} options={['IST +5:30', 'UTC', 'EST −5', 'PST −8']} />
            </Field>
          </>
        )}

        <Field label="Re-entry policy">
          <Sel value={cfg.reEntry} onChange={set('reEntry')} options={[
            { value: 'once_ever', label: 'Once ever (no re-entry)' },
            { value: 'always', label: 'Re-enter every time eligible' },
            { value: 'cooldown', label: 'Re-enter after a cooldown' },
            { value: 'once_per_occurrence', label: 'Once per scheduled occurrence' },
          ]} />
          <div className={styles.hint}>A user has one active trip at a time; re-entry restarts a completed trip. Enforced by the engine.</div>
        </Field>
        {cfg.reEntry === 'cooldown' && (
          <Field label="Cooldown">
            <div className={styles.row2}>
              <Inp value={cfg.cooldownValue} onChange={set('cooldownValue')} placeholder="30" type="number" />
              <Sel value={cfg.cooldownUnit} onChange={set('cooldownUnit')} options={['hours', 'days', 'weeks', 'months']} />
            </div>
            <div className={styles.hint}>Wait this long after a trip completes before the same user can re-enter.</div>
          </Field>
        )}

        <Field label="Context mapping (optional)">
          <Inp value={cfg.context} onChange={set('context')} placeholder="e.g. task_id, project_id" />
          <div className={styles.hint}>Comma-separated event property keys carried onto the trip (event entry). Goal &amp; global-exit checks can then match this exact context — so an unrelated task's event won't satisfy a task-specific goal.</div>
        </Field>

        <Field label="Global exit — on event">
          <Sel value={cfg.globalExitEvent} onChange={set('globalExitEvent')} options={[{ value: '', label: 'None' }, ...this.state.events.map((e) => ({ value: e, label: e }))]} />
          {cfg.globalExitEvent && (
            <label className={styles.hint} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!cfg.globalExitMatchContext} onChange={(e) => set('globalExitMatchContext')(e.target.checked)} />
              Only if the event matches this trip&apos;s context
            </label>
          )}
          <div className={styles.hint}>Leaves the journey immediately (any step) when this event fires — cancels pending waits and unsent messages.</div>
        </Field>

        <Field label="Global exit — max duration (optional)">
          <div className={styles.row2}>
            <Inp value={cfg.globalExitDuration} onChange={set('globalExitDuration')} placeholder="none" type="number" />
            <Sel value={cfg.globalExitDurationUnit} onChange={set('globalExitDurationUnit')} options={['minutes', 'hours', 'days', 'weeks']} />
          </div>
          <div className={styles.hint}>Force-exit a trip that has been in the journey longer than this.</div>
        </Field>

        <Field label="Sending rules (quiet hours)">
          <label className={styles.hint} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!cfg.quietEnabled} onChange={(e) => set('quietEnabled')(e.target.checked)} />
            Don&apos;t send during quiet hours (IST)
          </label>
          {cfg.quietEnabled && (
            <div className={styles.row2}>
              <Inp value={cfg.quietStart} onChange={set('quietStart')} type="number" placeholder="22" />
              <Inp value={cfg.quietEnd} onChange={set('quietEnd')} type="number" placeholder="8" />
            </div>
          )}
          <div className={styles.hint}>Start–end hour (IST, 0–23). Sends inside the window are held until it passes.</div>
        </Field>

        <Field label="Minimum spacing between messages (optional)">
          <div className={styles.row2}>
            <Inp value={cfg.minSpacingValue} onChange={set('minSpacingValue')} type="number" placeholder="none" />
            <Sel value={cfg.minSpacingUnit} onChange={set('minSpacingUnit')} options={['hours', 'days', 'weeks']} />
          </div>
          <div className={styles.hint}>Counts across ALL journeys and campaigns for the user (from the send ledger).</div>
        </Field>

        <Field label="Frequency cap (max messages / day, optional)">
          <div className={styles.row2}>
            <Inp value={cfg.capPerDay} onChange={set('capPerDay')} type="number" placeholder="none" />
            <Sel value={cfg.capChannel} onChange={set('capChannel')} options={[{ value: 'any', label: 'Any channel' }, { value: 'push', label: 'Push' }, { value: 'whatsapp', label: 'WhatsApp' }, { value: 'sms', label: 'SMS' }, { value: 'email', label: 'Email' }, { value: 'rcs', label: 'RCS' }]} />
          </div>
          <div className={styles.hint}>Cross-journey cap. On cap: <b>{cfg.onCap || 'skip'}</b>.</div>
        </Field>

        <Field label="On cap / WhatsApp de-dup">
          <div className={styles.row2}>
            <Sel value={cfg.onCap} onChange={set('onCap')} options={[{ value: 'skip', label: 'Skip (drop send)' }, { value: 'delay', label: 'Delay (retry later)' }]} />
            <Inp value={cfg.waDedupHours} onChange={set('waDedupHours')} type="number" placeholder="WA dedup hrs" />
          </div>
          <div className={styles.hint}>WhatsApp de-dup: don&apos;t re-send the same WA template to a user within this many hours.</div>
        </Field>
      </>
    );
  }

  // Per-send time-of-day window (IST), shared across all message-action nodes.
  _sendWindowSection(cfg, set) {
    return (
      <>
        <Field label="Send window (time of day)">
          <label className={styles.hint} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!cfg.sendWindowEnabled} onChange={(e) => set('sendWindowEnabled')(e.target.checked)} />
            Only send within a daily time window (IST)
          </label>
        </Field>
        {cfg.sendWindowEnabled && (
          <Field label="Between (hour of day, 0–24, IST)">
            <div className={styles.row2}>
              <Inp value={cfg.sendWindowStart} onChange={set('sendWindowStart')} type="number" placeholder="8" />
              <Inp value={cfg.sendWindowEnd} onChange={set('sendWindowEnd')} type="number" placeholder="18" />
            </div>
            <div className={styles.hint}>e.g. 8–18 = morning/day, 18–20 = evening. A send that lands outside the window is held until it opens.</div>
          </Field>
        )}
      </>
    );
  }

  // Repeat-on-Schedule section, shared across all message-action nodes.
  _repeatSection(cfg, set) {
    return (
      <>
        <Field label="Repeat on schedule">
          <label className={styles.hint} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!cfg.repeatEnabled} onChange={(e) => set('repeatEnabled')(e.target.checked)} />
            Re-send this message on a recurring schedule
          </label>
        </Field>
        {cfg.repeatEnabled && (
          <>
            <Field label="Frequency">
              <div className={styles.row2}>
                <Sel value={cfg.repeatEvery} onChange={set('repeatEvery')} options={[{ value: 'day', label: 'Every day' }, { value: 'week', label: 'Every week' }, { value: 'month', label: 'Every month' }]} />
                <Inp value={cfg.repeatAt} onChange={set('repeatAt')} type="time" />
              </div>
              <div className={styles.hint}>First send at the next occurrence of this time on/after entry, then each period. Never more than once per window; catches up one missed occurrence per tick.</div>
            </Field>
            <Field label="Stop after N occurrences (optional)">
              <Inp value={cfg.repeatMax} onChange={set('repeatMax')} placeholder="blank = until end date / indefinite" type="number" />
            </Field>
            <Field label="End date (optional)">
              <Inp value={cfg.repeatEndDate} onChange={set('repeatEndDate')} type="date" />
            </Field>
          </>
        )}
      </>
    );
  }

  _fields(type, cfg, set, onFieldChange, segments) {
    const { onChange, node } = this.props;
    switch (type) {
      case 'entry':
        return this._entryFields(cfg, set, segments);

      case 'entry-event':
        return (
          <>
            <Field label="Trigger Event">
              <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
            </Field>
            <Field label="Lookback window">
              <div className={styles.row2}>
                <Inp value={cfg.lookbackValue} onChange={set('lookbackValue')} placeholder="7" type="number" />
                <Sel value={cfg.lookbackUnit} onChange={set('lookbackUnit')} options={['hours','days','weeks','months']} />
              </div>
              <div className={styles.hint}>Enrolls users who fired this event within the window, checked every 15 min. Expect up to ~20 min from event to enrollment.</div>
            </Field>
            <Field label="Event properties (optional)">
              <PropertyFilters
                propFilters={cfg.propFilters || []}
                onChange={set('propFilters')}
                eventName={cfg.event}
                eventTable="events"
                idPrefix={node.id}
              />
              <div className={styles.hint}>Enforced by the journey engine — only users whose event matched every condition are enrolled.</div>
            </Field>
          </>
        );

      case 'entry-schedule':
        return (
          <>
            <Field label="Cron Expression">
              <Inp value={cfg.cron} onChange={set('cron')} placeholder="0 18 * * *" />
              <div className={styles.hint}>Checked every 15 min — schedules finer than that won't fire on every intended tick.</div>
            </Field>
            <Field label="Timezone">
              <Sel value={cfg.tz} onChange={set('tz')} options={['IST +5:30','UTC','EST −5','PST −8']} />
            </Field>
            <Field label="Segment">
              <Sel
                value={cfg.segmentId}
                onChange={set('segmentId')}
                options={segments.map(s => ({ value: s.id, label: s.name }))}
              />
              <div className={styles.hint}>Evaluated fresh each time the schedule fires.</div>
            </Field>
          </>
        );

      case 'entry-segment':
        return (
          <Field label="Segment">
            {/* Same component Campaigns' audience step uses — its own
                Saved Segment / Build Filter toggle covers both "just pick
                one" and "define custom conditions" without us needing two
                separate UIs here. Picking a saved segment updates
                segmentId/segmentName immediately, the same as the plain
                dropdown this replaced; building a filter only commits once
                explicitly saved (via the component's own Save bar) — that's
                what turns it into a real, reusable segment_id. */}
            <SegmentBuilder
              onChange={(audience) => {
                if (audience.mode === 'saved' && audience.selectedSegId) {
                  const seg = segments.find(s => s.id === audience.selectedSegId);
                  onChange({ ...cfg, segmentId: audience.selectedSegId, segmentName: seg?.name || '' });
                }
              }}
              onSaved={(segment) => onChange({ ...cfg, segmentId: segment.id, segmentName: segment.name })}
            />
          </Field>
        );

      case 'entry-api':
        return (
          <>
            <Field label="Description">
              <Inp value={cfg.description} onChange={set('description')} placeholder="e.g. External CRM trigger" />
            </Field>
            <Field label="Webhook Secret">
              <Inp value={cfg.secret} onChange={set('secret')} placeholder="Shared secret for callers to sign requests" />
            </Field>
          </>
        );

      case 'action-push':
        return <>{<PushFields {...cfg} onChange={onFieldChange} />}{this._sendWindowSection(cfg, set)}{this._repeatSection(cfg, set)}</>;

      case 'action-whatsapp':
        return <>{<WhatsappFields {...cfg} onChange={onFieldChange} />}{this._sendWindowSection(cfg, set)}{this._repeatSection(cfg, set)}</>;

      case 'action-email':
        return <>{<EmailFields {...cfg} onChange={onFieldChange} />}{this._sendWindowSection(cfg, set)}{this._repeatSection(cfg, set)}</>;

      case 'action-sms':
        return <>{<SmsFields {...cfg} onChange={onFieldChange} />}{this._sendWindowSection(cfg, set)}{this._repeatSection(cfg, set)}</>;

      case 'action-rcs':
        return <>{<RcsFields {...cfg} onChange={onFieldChange} />}{this._sendWindowSection(cfg, set)}{this._repeatSection(cfg, set)}</>;

      case 'action-webhook':
        return (
          <>
            <Field label="URL">
              <Inp value={cfg.url} onChange={set('url')} placeholder="https://api.example.com/…" />
            </Field>
            <Field label="Method">
              <Sel value={cfg.method} onChange={set('method')} options={['POST','GET','PUT','PATCH']} />
            </Field>
            <Field label="Payload (JSON)">
              <TextArea value={cfg.payload} onChange={set('payload')} placeholder='{"event":"…"}' rows={4} />
            </Field>
          </>
        );

      case 'wait-relative':
        return (
          <>
            <Field label="Duration">
              <div className={styles.row2}>
                <Inp value={cfg.amount} onChange={set('amount')} placeholder="1" type="number" />
                <Sel value={cfg.unit} onChange={set('unit')} options={['minutes','hours','days','weeks']} />
              </div>
            </Field>
          </>
        );

      case 'wait-absolute': {
        const WEEKDAYS = [
          { value: '0', label: 'Sunday' }, { value: '1', label: 'Monday' }, { value: '2', label: 'Tuesday' },
          { value: '3', label: 'Wednesday' }, { value: '4', label: 'Thursday' }, { value: '5', label: 'Friday' }, { value: '6', label: 'Saturday' },
        ];
        const needsTime = ['tomorrow', 'n_days', 'next_weekday', 'monthly_day', 'last_day_of_month'].includes(cfg.rule);
        return (
          <>
            <Field label="Rule">
              <Sel value={cfg.rule} onChange={set('rule')} options={[
                { value: 'next_day_noon',     label: 'Next day 12 PM' },
                { value: 'tomorrow',          label: 'Tomorrow at a time' },
                { value: 'n_days',            label: 'N days later at a time' },
                { value: 'next_weekday',      label: 'Next chosen weekday' },
                { value: 'next_monday',       label: 'Next Monday 9 AM' },
                { value: 'monthly_day',       label: 'Monthly on day N' },
                { value: 'last_day_of_month', label: 'Last day of month (or N before)' },
                { value: 'end_of_week',       label: 'End of week' },
                { value: 'specific',          label: 'A specific date & time' },
              ]} />
            </Field>
            {cfg.rule === 'n_days' && (
              <Field label="Days later"><Inp value={cfg.days} onChange={set('days')} placeholder="1" type="number" /></Field>
            )}
            {cfg.rule === 'next_weekday' && (
              <Field label="Weekday"><Sel value={cfg.weekday} onChange={set('weekday')} options={WEEKDAYS} /></Field>
            )}
            {cfg.rule === 'monthly_day' && (
              <Field label="Day of month"><Inp value={cfg.dayOfMonth} onChange={set('dayOfMonth')} placeholder="1" type="number" /><div className={styles.hint}>Clamped to the month length — day 31 lands on the last day in short months.</div></Field>
            )}
            {cfg.rule === 'last_day_of_month' && (
              <Field label="Days before month-end"><Inp value={cfg.daysBeforeEnd} onChange={set('daysBeforeEnd')} placeholder="0" type="number" /></Field>
            )}
            {needsTime && (
              <Field label="At time"><Inp value={cfg.time} onChange={set('time')} type="time" /></Field>
            )}
            {(cfg.rule === 'specific' || cfg.rule === 'custom') && (
              <Field label="Wait until">
                <div className={styles.row2}>
                  <Inp value={cfg.customDate} onChange={set('customDate')} type="date" />
                  <Inp value={cfg.customTime} onChange={set('customTime')} type="time" />
                </div>
              </Field>
            )}
            <div className={styles.hint}>{waitUntilPreview(cfg)}</div>
          </>
        );
      }

      case 'logic-condition': {
        const isDeliveryCheck = cfg.condType === 'if_delivered' || cfg.condType === 'if_failed';
        const isEventCheck = cfg.condType === 'event_performed' || cfg.condType === 'event_not_performed';
        const isUserProp = cfg.condType === 'user_property';
        return (
          <>
            <Field label="Condition">
              <Sel value={cfg.condType} onChange={set('condType')} options={[
                { value: 'if_not_opened',       label: "Hasn't opened previous step" },
                { value: 'if_delivered',        label: 'Previous step delivered' },
                { value: 'if_failed',           label: 'Previous step failed to send' },
                { value: 'event_performed',     label: 'Event performed (has done)' },
                { value: 'event_not_performed', label: 'Event NOT performed' },
                { value: 'user_property',       label: 'User attribute' },
              ]} />
              {isDeliveryCheck && (
                <div className={styles.hint}>
                  Must directly follow an action step (Push, SMS, Email, WhatsApp, or RCS) — checks that step's own send outcome, no event/time-window setup needed.
                  {cfg.condType === 'if_delivered'
                    ? ' Only Email confirms real delivery today; WhatsApp and Push have no delivery webhook yet and will never resolve "delivered." Add a wait before this step to give slower channels (SMS, RCS) time to report.'
                    : ' Reliable immediately for every channel — a send that fails at the API level (bad config, no contact info) is known right away, no wait needed.'}
                </div>
              )}
              {cfg.condType === 'if_not_opened' && (
                <div className={styles.hint}>True when the user hasn't opened any message from this journey yet.</div>
              )}
            </Field>

            {isEventCheck && (
              <>
                <Field label="Event">
                  <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
                  <div className={styles.hint}>Yes branch = the user {cfg.condType === 'event_performed' ? 'HAS' : 'has NOT'} done this event. Enforced by the engine.</div>
                </Field>
                <Field label="Within (optional)">
                  <div className={styles.row2}>
                    <Inp value={cfg.windowValue} onChange={set('windowValue')} placeholder="any time" type="number" />
                    <Sel value={cfg.windowUnit} onChange={set('windowUnit')} options={['hours', 'days', 'weeks', 'months']} />
                  </div>
                  <div className={styles.hint}>Blank = ever. Otherwise only counts events in this recent window.</div>
                </Field>
                <Field label="Event properties (optional)">
                  <PropertyFilters
                    propFilters={cfg.propFilters || []}
                    onChange={set('propFilters')}
                    eventName={cfg.event}
                    eventTable="events"
                    idPrefix={node.id}
                  />
                </Field>
              </>
            )}

            {isUserProp && (
              <>
                <Field label="Attribute">
                  <Sel value={cfg.attrField} onChange={set('attrField')} options={[
                    { value: 'location_city', label: 'City' },
                    { value: 'location_state', label: 'State' },
                    { value: 'location_country', label: 'Country' },
                    { value: 'mobile_platform', label: 'Platform' },
                    { value: 'language', label: 'Language' },
                    { value: 'kyc_status', label: 'KYC status' },
                    { value: 'custom_field_1', label: 'Badge level' },
                    { value: 'display_user_id', label: 'PickMyWork ID' },
                  ]} />
                </Field>
                <Field label="Operator">
                  <Sel value={cfg.attrOperator} onChange={set('attrOperator')} options={[
                    { value: 'equals', label: 'equals' },
                    { value: 'not_equals', label: 'not equals' },
                    { value: 'greater_than', label: 'greater than' },
                    { value: 'less_than', label: 'less than' },
                  ]} />
                </Field>
                <Field label="Value">
                  <Inp value={cfg.attrValue} onChange={set('attrValue')} placeholder="e.g. Mumbai / Gold / iOS" />
                  <div className={styles.hint}>Checked against the user's profile (same fields as Segments). Enforced by the engine.</div>
                </Field>
              </>
            )}
          </>
        );
      }

      case 'logic-ab':
        return (
          <>
            <Field label="Variant A %">
              <Inp value={cfg.splitA} onChange={set('splitA')} placeholder="50" type="number" />
            </Field>
            <Field label="Variant B %">
              <Inp value={cfg.splitB} onChange={set('splitB')} placeholder="50" type="number" />
            </Field>
          </>
        );

      case 'logic-goal':
        return (
          <Field label="Goal Event">
            <Sel value={cfg.event} onChange={set('event')} options={this.state.events} />
          </Field>
        );

      case 'logic-exit':
        return (
          <Field label="Exit Reason">
            <Sel value={cfg.reason} onChange={set('reason')} options={[
              { value: 'journey_complete', label: 'Journey complete' },
              { value: 'goal_achieved',    label: 'Goal achieved' },
              { value: 'unsubscribed',     label: 'User unsubscribed' },
            ]} />
          </Field>
        );

      default:
        return <div className={styles.noConfig}>No configuration needed.</div>;
    }
  }
}

export default ConfigDrawer;
